import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon, EditIcon, CheckIcon, XIcon, AlertTriangleIcon, LockIcon, UnlockIcon, FilterIcon, RefreshCwIcon } from 'lucide-react';
export function StatusManagement({
  locale = 'en-US'
}) {
  // Sample status data - in a real app, this would come from an API
  const [statuses, setStatuses] = useState([{
    id: 'status-001',
    label: 'Draft',
    color: '#E5E7EB',
    isSystem: true,
    category: 'document',
    usageCount: 452
  }, {
    id: 'status-002',
    label: 'Pending Approval',
    color: '#FEF3C7',
    isSystem: true,
    category: 'workflow',
    usageCount: 128
  }, {
    id: 'status-003',
    label: 'Approved',
    color: '#D1FAE5',
    isSystem: true,
    category: 'workflow',
    usageCount: 356
  }, {
    id: 'status-004',
    label: 'Rejected',
    color: '#FEE2E2',
    isSystem: true,
    category: 'workflow',
    usageCount: 73
  }, {
    id: 'status-005',
    label: 'Completed',
    color: '#DBEAFE',
    isSystem: true,
    category: 'document',
    usageCount: 289
  }, {
    id: 'status-006',
    label: 'On Hold',
    color: '#E0E7FF',
    isSystem: false,
    category: 'custom',
    usageCount: 42
  }, {
    id: 'status-007',
    label: 'Expired',
    color: '#FCE7F3',
    isSystem: false,
    category: 'document',
    usageCount: 18
  }, {
    id: 'status-008',
    label: 'In Progress',
    color: '#FEF3C7',
    isSystem: true,
    category: 'workflow',
    usageCount: 205
  }, {
    id: 'status-009',
    label: 'Cancelled',
    color: '#F3F4F6',
    isSystem: true,
    category: 'document',
    usageCount: 97
  }, {
    id: 'status-010',
    label: 'Needs Attention',
    color: '#FEE2E2',
    isSystem: false,
    category: 'custom',
    usageCount: 31
  }, {
    id: 'status-011',
    label: 'Awaiting Payment',
    color: '#FEF3C7',
    isSystem: true,
    category: 'financial',
    usageCount: 184
  }, {
    id: 'status-012',
    label: 'Paid',
    color: '#D1FAE5',
    isSystem: true,
    category: 'financial',
    usageCount: 263
  }, {
    id: 'status-013',
    label: 'Partially Paid',
    color: '#DBEAFE',
    isSystem: true,
    category: 'financial',
    usageCount: 87
  }, {
    id: 'status-014',
    label: 'Under Review',
    color: '#E0E7FF',
    isSystem: false,
    category: 'custom',
    usageCount: 56
  }]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingStatus, setEditingStatus] = useState(null);
  const [newStatus, setNewStatus] = useState({
    label: '',
    color: '#E5E7EB',
    category: 'custom'
  });
  const [showNewForm, setShowNewForm] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [systemFilter, setSystemFilter] = useState('all');
  const [sortBy, setSortBy] = useState('label');
  const [sortDirection, setSortDirection] = useState('asc');
  // Available status categories
  const categories = [{
    id: 'all',
    name: 'All Categories'
  }, {
    id: 'document',
    name: 'Document'
  }, {
    id: 'workflow',
    name: 'Workflow'
  }, {
    id: 'financial',
    name: 'Financial'
  }, {
    id: 'custom',
    name: 'Custom'
  }];
  // Filter and sort statuses
  const filteredStatuses = statuses.filter(status => {
    // Apply search filter
    const matchesSearch = status.label.toLowerCase().includes(searchTerm.toLowerCase());
    // Apply category filter
    const matchesCategory = categoryFilter === 'all' || status.category === categoryFilter;
    // Apply system/custom filter
    const matchesSystemFilter = systemFilter === 'all' || systemFilter === 'system' && status.isSystem || systemFilter === 'custom' && !status.isSystem;
    return matchesSearch && matchesCategory && matchesSystemFilter;
  }).sort((a, b) => {
    // Apply sorting
    if (sortBy === 'label') {
      return sortDirection === 'asc' ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label);
    } else if (sortBy === 'usage') {
      return sortDirection === 'asc' ? a.usageCount - b.usageCount : b.usageCount - a.usageCount;
    }
    return 0;
  });
  // Handle editing a status
  const startEditing = status => {
    setEditingStatus({
      ...status
    });
  };
  const cancelEditing = () => {
    setEditingStatus(null);
  };
  const saveEditedStatus = () => {
    if (!editingStatus.label.trim()) {
      alert('Status label cannot be empty');
      return;
    }
    setStatuses(statuses.map(status => status.id === editingStatus.id ? {
      ...status,
      label: editingStatus.label,
      color: editingStatus.color,
      category: editingStatus.isSystem ? status.category : editingStatus.category
    } : status));
    setEditingStatus(null);
  };
  // Handle creating a new status
  const handleCreateStatus = () => {
    if (!newStatus.label.trim()) {
      alert('Status label cannot be empty');
      return;
    }
    const newId = `status-${String(statuses.length + 1).padStart(3, '0')}`;
    setStatuses([...statuses, {
      id: newId,
      label: newStatus.label,
      color: newStatus.color,
      isSystem: false,
      category: newStatus.category,
      usageCount: 0
    }]);
    setNewStatus({
      label: '',
      color: '#E5E7EB',
      category: 'custom'
    });
    setShowNewForm(false);
  };
  // Handle deleting a status
  const handleDeleteStatus = id => {
    const statusToDelete = statuses.find(status => status.id === id);
    if (statusToDelete.isSystem) {
      alert('System statuses cannot be deleted');
      return;
    }
    if (statusToDelete.usageCount > 0) {
      const confirmDelete = window.confirm(`This status is used in ${statusToDelete.usageCount} places. Are you sure you want to delete it?`);
      if (!confirmDelete) return;
    }
    setStatuses(statuses.filter(status => status.id !== id));
  };
  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setCategoryFilter('all');
    setSystemFilter('all');
    setSortBy('label');
    setSortDirection('asc');
  };
  // Toggle sort direction
  const toggleSort = field => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
  };
  return <div data-id="element-20104">
      <div className="mb-8" data-id="element-20105">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-20106">Status Management</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-20107">
          Manage statuses used throughout the system
        </p>
      </div>
      {/* Search and Filter Bar */}
      <div className="bg-white shadow rounded-lg mb-6" data-id="element-20108">
        <div className="p-4 border-b border-gray-200" data-id="element-20109">
          <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4" data-id="element-20110">
            <div className="relative flex-grow" data-id="element-20111">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-20112">
                <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-20113" />
              </div>
              <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search statuses..." data-id="element-20114" />
            </div>
            <div className="flex space-x-2" data-id="element-20115">
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20116">
                {categories.map(category => <option key={category.id} value={category.id} data-id="element-20117">
                    {category.name}
                  </option>)}
              </select>
              <select value={systemFilter} onChange={e => setSystemFilter(e.target.value)} className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-20118">
                <option value="all" data-id="element-20119">All Statuses</option>
                <option value="system" data-id="element-20120">System Only</option>
                <option value="custom" data-id="element-20121">Custom Only</option>
              </select>
              <button type="button" onClick={resetFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20122">
                <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-20123" />
                Reset
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Status List */}
      <div className="bg-white shadow rounded-lg overflow-hidden" data-id="element-20124">
        <div className="flex justify-between items-center p-4 border-b border-gray-200" data-id="element-20125">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-20126">Statuses</h3>
          <button type="button" onClick={() => setShowNewForm(!showNewForm)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20127">
            <PlusIcon className="h-4 w-4 mr-1" data-id="element-20128" />
            Add Status
          </button>
        </div>
        {/* New Status Form */}
        {showNewForm && <div className="p-4 bg-gray-50 border-b border-gray-200" data-id="element-20129">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-20130">
              <div data-id="element-20131">
                <label htmlFor="new-status-label" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20132">
                  Status Label
                </label>
                <input type="text" id="new-status-label" value={newStatus.label} onChange={e => setNewStatus({
              ...newStatus,
              label: e.target.value
            })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Enter status label" data-id="element-20133" />
              </div>
              <div data-id="element-20134">
                <label htmlFor="new-status-color" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20135">
                  Color
                </label>
                <div className="flex items-center" data-id="element-20136">
                  <input type="color" id="new-status-color" value={newStatus.color} onChange={e => setNewStatus({
                ...newStatus,
                color: e.target.value
              })} className="h-8 w-8 border-gray-300 rounded-md shadow-sm mr-2" data-id="element-20137" />
                  <input type="text" value={newStatus.color} onChange={e => setNewStatus({
                ...newStatus,
                color: e.target.value
              })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20138" />
                </div>
              </div>
              <div data-id="element-20139">
                <label htmlFor="new-status-category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-20140">
                  Category
                </label>
                <select id="new-status-category" value={newStatus.category} onChange={e => setNewStatus({
              ...newStatus,
              category: e.target.value
            })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20141">
                  <option value="document" data-id="element-20142">Document</option>
                  <option value="workflow" data-id="element-20143">Workflow</option>
                  <option value="financial" data-id="element-20144">Financial</option>
                  <option value="custom" data-id="element-20145">Custom</option>
                </select>
              </div>
              <div className="flex items-end space-x-2" data-id="element-20146">
                <button type="button" onClick={handleCreateStatus} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20147">
                  <CheckIcon className="h-4 w-4 mr-1" data-id="element-20148" />
                  Save
                </button>
                <button type="button" onClick={() => setShowNewForm(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-20149">
                  <XIcon className="h-4 w-4 mr-1" data-id="element-20150" />
                  Cancel
                </button>
              </div>
            </div>
          </div>}
        {/* Status Table */}
        <div className="overflow-x-auto" data-id="element-20151">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-20152">
            <thead className="bg-gray-50" data-id="element-20153">
              <tr data-id="element-20154">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('label')} data-id="element-20155">
                  <div className="flex items-center" data-id="element-20156">
                    Status
                    {sortBy === 'label' && <span className="ml-1" data-id="element-20157">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20158">
                  Preview
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20159">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20160">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => toggleSort('usage')} data-id="element-20161">
                  <div className="flex items-center" data-id="element-20162">
                    Usage
                    {sortBy === 'usage' && <span className="ml-1" data-id="element-20163">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-20164">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-20165">
              {filteredStatuses.length === 0 ? <tr data-id="element-20166">
                  <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-20167">
                    No statuses found matching your filters
                  </td>
                </tr> : filteredStatuses.map(status => <tr key={status.id} data-id="element-20168">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20169">
                      {editingStatus && editingStatus.id === status.id ? <input type="text" value={editingStatus.label} onChange={e => setEditingStatus({
                  ...editingStatus,
                  label: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20170" /> : <div className="text-sm font-medium text-gray-900" data-id="element-20171">
                          {status.label}
                        </div>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20172">
                      {editingStatus && editingStatus.id === status.id ? <div className="flex items-center" data-id="element-20173">
                          <input type="color" value={editingStatus.color} onChange={e => setEditingStatus({
                    ...editingStatus,
                    color: e.target.value
                  })} className="h-8 w-8 border-gray-300 rounded-md shadow-sm mr-2" data-id="element-20174" />
                          <input type="text" value={editingStatus.color} onChange={e => setEditingStatus({
                    ...editingStatus,
                    color: e.target.value
                  })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-24 sm:text-sm border-gray-300 rounded-md" data-id="element-20175" />
                        </div> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{
                  backgroundColor: status.color,
                  color: getContrastColor(status.color)
                }} data-id="element-20176">
                          {status.label}
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20177">
                      {editingStatus && editingStatus.id === status.id && !status.isSystem ? <select value={editingStatus.category} onChange={e => setEditingStatus({
                  ...editingStatus,
                  category: e.target.value
                })} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-20178">
                          <option value="document" data-id="element-20179">Document</option>
                          <option value="workflow" data-id="element-20180">Workflow</option>
                          <option value="financial" data-id="element-20181">Financial</option>
                          <option value="custom" data-id="element-20182">Custom</option>
                        </select> : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800" data-id="element-20183">
                          {categories.find(c => c.id === status.category)?.name || status.category}
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-20184">
                      <div className="flex items-center" data-id="element-20185">
                        {status.isSystem ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-20186">
                            <LockIcon className="h-3 w-3 mr-1" data-id="element-20187" />
                            System
                          </span> : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-20188">
                            <UnlockIcon className="h-3 w-3 mr-1" data-id="element-20189" />
                            Custom
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-20190">
                      {status.usageCount}{' '}
                      {status.usageCount === 1 ? 'use' : 'uses'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-20191">
                      {editingStatus && editingStatus.id === status.id ? <div className="flex space-x-2 justify-end" data-id="element-20192">
                          <button onClick={saveEditedStatus} className="text-indigo-600 hover:text-indigo-900" data-id="element-20193">
                            <CheckIcon className="h-5 w-5" data-id="element-20194" />
                          </button>
                          <button onClick={cancelEditing} className="text-gray-600 hover:text-gray-900" data-id="element-20195">
                            <XIcon className="h-5 w-5" data-id="element-20196" />
                          </button>
                        </div> : <div className="flex space-x-2 justify-end" data-id="element-20197">
                          <button onClick={() => startEditing(status)} className="text-indigo-600 hover:text-indigo-900" data-id="element-20198">
                            <EditIcon className="h-5 w-5" data-id="element-20199" />
                          </button>
                          {!status.isSystem && <button onClick={() => handleDeleteStatus(status.id)} className="text-red-600 hover:text-red-900" disabled={status.isSystem} data-id="element-20200">
                              <TrashIcon className="h-5 w-5" data-id="element-20201" />
                            </button>}
                        </div>}
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}
// Helper function to determine text color based on background color
function getContrastColor(hexColor) {
  // Convert hex to RGB
  let r = parseInt(hexColor.substr(1, 2), 16);
  let g = parseInt(hexColor.substr(3, 2), 16);
  let b = parseInt(hexColor.substr(5, 2), 16);
  // Calculate luminance
  let luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  // Return black or white depending on luminance
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}